import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { MediaImageProps } from '@elfsight/widget-media';
import type { LoadImageResult } from '@elfsight/widget-utils';
import { truncateText } from '@elfsight/widget-utils';
import { useLayout } from '../../layout';
import type { BlockProps } from '../../lib/card';
import { CardLayout } from '../../lib/card';
import { themeColors } from '../../themes';
import { useSocialFeedSettings } from '../../utils';
import { Media } from '../components';
import { MediaSkeleton } from '../components/media';
import { usePost } from '../Post';
import { useMedia, usePopupMediaRatio, usePostSettings } from '../utils';
import { CardMediaTypeLayer } from './CardMediaTypeLayer';

const DEFAULT_VIDEO_RATIO = 0.66;

type MediaStateType = Omit<LoadImageResult, 'src'>;

type OnLoadHandlerType = NonNullable<MediaImageProps['onLoad']>;

interface CardMediaBlockProps extends BlockProps {
  mode?: 'contain' | 'cover' | 'scale-down' | 'none';
  borderRadius?: number;
  popupMediaBackgroundColor?: string;
  ratio?: number;
  hideMediaType?: boolean;
  thumbnail?: boolean;
}

export function CardMediaBlock({
  mode,
  borderRadius,
  popupMediaBackgroundColor,
  ratio = 1,
  hideMediaType = false,
  thumbnail = false,
  ...forwardingProps
}: CardMediaBlockProps) {
  const { layout } = useSocialFeedSettings();
  const {
    isInPopup,
    text = '',
    rect: { width }
  } = usePost();
  const { heightNormalization, textColor } = usePostSettings();
  const { rerender } = useLayout();

  const [mediaState, setMediaState] = useState<MediaStateType>({
    status: 'pending',
    width: undefined,
    height: undefined
  });
  const { status, width: mediaWidth, height: mediaHeight } = mediaState;

  const loadedRatio =
    mediaWidth && mediaHeight ? mediaHeight / mediaWidth : undefined;

  const media = useMedia();
  const isVideo = media?.type === 'video';

  const finalRatio = usePopupMediaRatio({
    defaultRatio: ratio,
    loadedRatio
  });

  const fallbackRatio = isVideo ? DEFAULT_VIDEO_RATIO : undefined;
  const effectiveRatio = finalRatio || fallbackRatio;

  // @ts-expect-error Проблемы с конфликтом типа ReactEventHandler<HTMLDivElement>
  const handleLoad: OnLoadHandlerType = (_event, size) => {
    setMediaState((prev) => ({ ...prev, status: 'loaded', ...size }));

    if (!rerender) return;

    requestAnimationFrame(() => {
      rerender();
    });
  };

  const handleError = () =>
    setMediaState((prev) => ({ ...prev, status: 'failed' }));

  const isPending = status === 'pending';

  const aspectRatio =
    effectiveRatio && isInPopup && isPending ? 1 / effectiveRatio : undefined;

  const isShowSkeleton = isPending && isInPopup && Boolean(effectiveRatio);

  const backgroundImage =
    heightNormalization && status === 'loaded'
      ? media.thumbnail || media.src
      : undefined;

  const hideInOneColumnCase =
    !isInPopup && !width && (layout === 'list' || layout === 'slider');

  if (status === 'failed' || hideInOneColumnCase) {
    return null;
  }

  return (
    <MediaBlock
      {...forwardingProps}
      _aspectRatio={aspectRatio}
      _backgroundImage={backgroundImage}
      _column
      _center
    >
      {isShowSkeleton && (
        <MediaSkeleton backgroundColor={popupMediaBackgroundColor} />
      )}

      <Media
        alt={truncateText(text)}
        ratio={finalRatio}
        mode={mode || (thumbnail ? 'cover' : 'contain')}
        media={media}
        borderRadius={borderRadius}
        backgroundColor={
          isPending && isInPopup ? undefined : popupMediaBackgroundColor
        }
        thumbnail={thumbnail}
        onError={handleError}
        onLoad={handleLoad}
        loaderColor={textColor}
      />

      {!hideMediaType && thumbnail && (media?.src || media?.thumbnail) && (
        <CardMediaTypeLayer />
      )}
    </MediaBlock>
  );
}

const MediaBlock = styled(CardLayout.OuterBlock)<{
  _aspectRatio?: number;
  _backgroundImage?: string;
}>`
  position: relative;
  overflow: hidden;
  max-height: 100%;
  ${({ _aspectRatio }) => _aspectRatio && `aspect-ratio: ${_aspectRatio}`};

  ${({ _backgroundImage }) =>
    _backgroundImage &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: url(${_backgroundImage}) ${themeColors.blackColor07};
        background-size: cover;
        background-position: center;
        filter: blur(10px) brightness(0.5);
        transform: scale(1.2);
        z-index: 0;
      }
    `};
`;
